import { useEffect } from 'react'

import type { CameraVideoTrackInitConfig } from 'agora-rtc-sdk-ng'

import type { AgoraRTCClientWrapper } from '@core/agoraRTC/libs/AgoraRTCClientWrapper'
import { logError } from '@core/logging'

import { useCamera } from './useCamera'
import { useClientRole } from './useClientRole'
import { useConnectionState } from './useConnectionState'
import { useMicrophone } from './useMicrophone'
import { useVirtualBackground } from './useVirtualBackground'

export interface UsePublishLocalTrackProps {
  agoraClient: AgoraRTCClientWrapper | undefined
  onPublishError?: (type: string) => void
  microphoneMuted: boolean
  cameraEnabled: boolean
  cameraVideoTrackInitConfig?: Omit<
    CameraVideoTrackInitConfig,
    'cameraId' | 'facingMode'
  > // This object is a useEffect dependency. Ensure the reference doesn't change with each render to avoid excess useEffect calls.
  virtualBackground?: 'blur' | undefined | { type: 'image'; url: string }
}

/**
 * Hook that publish local tracks when connected to the Agora channel.
 * It automatically publishes audio and video tracks when they change.
 * Optionally applies virtual background to the video track.
 */
export function usePublishLocalTrack({
  agoraClient,
  onPublishError,
  microphoneMuted,
  cameraEnabled,
  cameraVideoTrackInitConfig,
  virtualBackground,
}: UsePublishLocalTrackProps) {
  const connectionState = useConnectionState(agoraClient)
  const clientRole = useClientRole(agoraClient)
  const audioTrack = useMicrophone(!!agoraClient && !microphoneMuted)
  const videoTrack = useCamera({
    allowed: !!agoraClient && cameraEnabled,
    config: cameraVideoTrackInitConfig,
  })
  const isHost = clientRole === 'host'

  // Apply virtual background to local video track
  useVirtualBackground({
    videoTrack,
    virtualBackground,
  })

  useEffect(() => {
    if (connectionState === 'CONNECTED' && audioTrack && isHost) {
      audioTrack?.setEnabled(!microphoneMuted).then(() => {
        if (audioTrack.enabled) {
          agoraClient?.publish(audioTrack).catch(() => {
            onPublishError?.('audio')
          })
        }
      })
    }
    return () => {
      if (connectionState === 'CONNECTED' && audioTrack && isHost) {
        agoraClient?.unpublish(audioTrack).catch(() => {
          logError(new Error('Failed to unpublish video track'))
        })
      }
    }
  }, [
    agoraClient,
    audioTrack,
    connectionState,
    isHost,
    microphoneMuted,
    onPublishError,
  ])

  useEffect(() => {
    if (connectionState === 'CONNECTED' && videoTrack && isHost) {
      videoTrack?.setEnabled(cameraEnabled).then(() => {
        if (videoTrack.enabled) {
          agoraClient?.publish(videoTrack).catch(() => {
            onPublishError?.('video')
          })
        }
      })
    }
    return () => {
      if (connectionState === 'CONNECTED' && videoTrack && isHost) {
        agoraClient?.unpublish(videoTrack).catch(() => {
          logError(new Error('Failed to unpublish video track'))
        })
      }
    }
  }, [
    agoraClient,
    connectionState,
    isHost,
    videoTrack,
    cameraEnabled,
    onPublishError,
  ])

  return { audioTrack, videoTrack }
}
